import {useState, useEffect, useRef, useContext } from 'react';
import CMSContext from 'context/CMSContext.js';
import { usePreviousUrl } from '../../context/PreviousUrlContext'
import { pushPageViewEvent } from '../tracking/events';

const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });
  return width;
};

const useComponentVisible = (initialIsVisible) => {
    const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible);
    const ref = useRef(null);
  
    const handleHideDropdown = (event) => {
      if (event.key === "Escape") {
        setIsComponentVisible(false);
      }
    };
  
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsComponentVisible(false);
      }
    };
  
    useEffect(() => {
      document.addEventListener("keydown", handleHideDropdown, true);
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("keydown", handleHideDropdown, true);
        document.removeEventListener("click", handleClickOutside, true);
      };
    });
  
    return { ref, isComponentVisible, setIsComponentVisible };
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
const useClickOutside = (ref, onClickOutside) => {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
};

const useTranslation = (snippetId) => {
    const cms = useContext(CMSContext);
    return cms?.snippets?.[snippetId]?.text || '';
}

/**
 * 
 * @param {object} usePageViewTrackingProps - An object with the following properties
 * @param {{currentUri?: string, pageTitle?: string, lastUri?: string}} usePageViewTrackingProps.pageViewEventData - If requires explicit pageViewEventData you can pass it here otherwise default calculated values are being used
 * @param {any} usePageViewTrackingProps.externalPageLoadedState - If you have external page loaded state you can pass it here otherwise use false or undefined
 */
const usePageViewTracking = ({externalPageLoadedState, pageViewEventData = {}} = {}) => {
  const previousUrl = usePreviousUrl(); // Grab the previous URL
  const [isComponentLoaded, setIsComponentLoaded] = useState(false);
  const isPageLoaded = externalPageLoadedState !== undefined ? externalPageLoadedState : isComponentLoaded;// Capture page loaded state from external or internal state

  useEffect(() => {
    if(isPageLoaded){
       // Data Tracking send page view event
       sessionStorage.setItem('current_referrer', previousUrl);
       pushPageViewEvent({lastUri: previousUrl, ...pageViewEventData});
    }
  }, [previousUrl, isPageLoaded]);

  useEffect(() => {
      setIsComponentLoaded(true);
  },[]);
}


export { useInterval, useWindowWidth, useComponentVisible, useClickOutside, useTranslation, usePageViewTracking };